
import { useCallback, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import {
  CreateListingInputSchema,
  ListingAddressInputSchema,
  UnavailabilityBlockInputSchema,
  BookingAccessInputSchema,
  BookingUntilSchema,
  DEFAULT_BOOKING_HORIZON_DAYS,
  MIN_BOOKING_HORIZON_DAYS,
  MAX_BOOKING_HORIZON_DAYS,
  BookingAccessKind,
  type CreateListingInput,
  type BookingAccessInput,
} from '@shared/listing';
import type { ReactFormExtendedApi } from '@tanstack/react-form';
import { useForm } from '@client/shared/form';
import { zodValidator } from '@client/shared/form';
import { addDays, todayInZone } from '@client/shared/lib/wall-clock-intake.ts';
import { messageFor } from '@client/shared/lib/error-message.ts';
import { runCreateListing, type CreatedListing } from './new-listing-mutation.ts';
import { LISTINGS_QUERY_PREFIX } from '../Listings/listings-operations.ts';
import { BILLING_STATUS_QUERY_KEY } from '@client/features/group';
import type { ReasonedRange } from './unavailability-ranges.ts';

export const STEPS = ['basics', 'availability', 'access'] as const;
export type Step = (typeof STEPS)[number];

export const STEP_META: Record<Step, { title: string; blurb: string }> = {
  basics: { title: 'Basics', blurb: 'Where the open house is, and how to get in.' },
  availability: { title: 'Availability', blurb: 'How far ahead agents can book, and when it is blocked.' },
  access: { title: 'Access & notifications', blurb: 'Who can book it, and who hears about each booking.' },
};

export interface NewListingFormValues {
  address: {
    formatted: string;
    line1?: string;
    line2?: string;
    city?: string;
    region?: string;
    postalCode?: string;
    country?: string;
    placeId?: string;
    lat?: number;
    lng?: number;
    timezone?: string;
  };
  showingNotes: string;
  // Mutually-exclusive booking-window modes: 'days' uses bookingHorizonDays, 'date' uses bookingUntil.
  bookingWindowMode: BookingWindowMode;
  bookingHorizonDays: number;
  bookingUntil: string;
  unavailability: ReasonedRange[];
  bookingAccess: BookingAccessInput;
  notifyUserIds: string[];
}

export const BookingWindowMode = { Days: 'days', Date: 'date' } as const;
export type BookingWindowMode = (typeof BookingWindowMode)[keyof typeof BookingWindowMode];

export type ListingAddressDraft = NewListingFormValues['address'];

export function browserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

export function emptyFormValues(): NewListingFormValues {
  return {
    address: { formatted: '', timezone: browserTimeZone() },
    showingNotes: '',
    bookingWindowMode: BookingWindowMode.Date,
    bookingHorizonDays: DEFAULT_BOOKING_HORIZON_DAYS,
    bookingUntil: addDays(todayInZone(), 7),
    unavailability: [],
    bookingAccess: { kind: BookingAccessKind.AllAgents },
    notifyUserIds: [],
  };
}

// Only the active mode's field is enforced; the fixed date reuses the shared @shared/listing rule.
function refineBookingWindow(
  v: { bookingWindowMode: BookingWindowMode; bookingUntil: string },
  ctx: z.RefinementCtx,
): void {
  if (v.bookingWindowMode !== BookingWindowMode.Date) return;
  const result = BookingUntilSchema.safeParse(v.bookingUntil);
  if (!result.success) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: result.error.issues[0]?.message ?? 'Enter a valid date.',
      path: ['bookingUntil'],
    });
  }
}

const FormValuesSchema: z.ZodType<NewListingFormValues> = z
  .object({
    address: ListingAddressInputSchema,
    showingNotes: z.string().trim().max(5000, 'Showing notes are too long.'),
    bookingWindowMode: z.enum([BookingWindowMode.Days, BookingWindowMode.Date]),
    bookingHorizonDays: z
      .number()
      .int('The booking horizon must be a whole number of days.')
      .min(MIN_BOOKING_HORIZON_DAYS, `The booking horizon must be at least ${MIN_BOOKING_HORIZON_DAYS} days.`)
      .max(MAX_BOOKING_HORIZON_DAYS, `The booking horizon cannot exceed ${MAX_BOOKING_HORIZON_DAYS} days.`),
    bookingUntil: z.string(),
    unavailability: z.array(UnavailabilityBlockInputSchema),
    bookingAccess: BookingAccessInputSchema,
    notifyUserIds: z.array(z.string().uuid('Each notify recipient must be a valid user id.')),
  })
  .superRefine(refineBookingWindow) as z.ZodType<NewListingFormValues>;

const stepSchemas: Record<Step, z.ZodType> = {
  basics: z.object({
    address: ListingAddressInputSchema,
    showingNotes: CreateListingInputSchema.shape.showingNotes,
  }),
  availability: z
    .object({
      bookingWindowMode: z.enum([BookingWindowMode.Days, BookingWindowMode.Date]),
      bookingHorizonDays: CreateListingInputSchema.shape.bookingHorizonDays,
      bookingUntil: z.string(),
      unavailability: CreateListingInputSchema.shape.unavailability,
    })
    .superRefine(refineBookingWindow),
  access: z.object({
    bookingAccess: CreateListingInputSchema.shape.bookingAccess,
    notifyUserIds: CreateListingInputSchema.shape.notifyUserIds,
  }),
};

function validateStep(step: Step, values: NewListingFormValues): string | undefined {
  const result = stepSchemas[step].safeParse(values);
  if (result.success) return undefined;
  return result.error.issues[0]?.message ?? 'Please fix the highlighted fields.';
}

export type NewListingFormApi = ReactFormExtendedApi<NewListingFormValues, undefined>;

export interface ListingFormMachine {
  form: NewListingFormApi;
  step: Step;
  stepIndex: number;
  isFirstStep: boolean;
  isLastStep: boolean;
  next: () => boolean;
  back: () => void;
  goTo: (step: Step) => void;
  stepError: string | undefined;
  submitError: string | undefined;
  submitting: boolean;
}

export interface UseListingFormOptions {
  initialValues: NewListingFormValues;
  submit: (values: NewListingFormValues) => Promise<void>;
}

export function useListingForm({ initialValues, submit }: UseListingFormOptions): ListingFormMachine {
  const [stepIndex, setStepIndex] = useState(0);
  const [stepError, setStepError] = useState<string | undefined>(undefined);
  const [submitError, setSubmitError] = useState<string | undefined>(undefined);
  const [submitting, setSubmitting] = useState(false);

  const step = STEPS[stepIndex]!;

  const form = useForm({
    defaultValues: initialValues,
    validators: { onSubmit: zodValidator(FormValuesSchema) },
    onSubmit: async ({ value }) => {
      setSubmitError(undefined);
      setSubmitting(true);
      try {
        await submit(value);
      } catch (err) {
        setSubmitError(messageFor(err, 'Something went wrong saving the listing. Please try again.'));
      } finally {
        setSubmitting(false);
      }
    },
  });

  const next = useCallback((): boolean => {
    const values = form.state.values;
    const error = validateStep(step, values);
    if (error) {
      setStepError(error);
      void form.validateAllFields('change');
      return false;
    }
    setStepError(undefined);
    setStepIndex((i) => Math.min(i + 1, STEPS.length - 1));
    return true;
  }, [form, step]);

  const back = useCallback(() => {
    setStepError(undefined);
    setStepIndex((i) => Math.max(i - 1, 0));
  }, []);

  const goTo = useCallback(
    (target: Step) => {
      const targetIndex = STEPS.indexOf(target);
      if (targetIndex <= stepIndex) {
        setStepError(undefined);
        setStepIndex(targetIndex);
      }
    },
    [stepIndex],
  );

  return useMemo(
    () => ({
      form,
      step,
      stepIndex,
      isFirstStep: stepIndex === 0,
      isLastStep: stepIndex === STEPS.length - 1,
      next,
      back,
      goTo,
      stepError,
      submitError,
      submitting,
    }),
    [form, step, stepIndex, next, back, goTo, stepError, submitError, submitting],
  );
}

export interface UseNewListingForm extends ListingFormMachine {
  created: CreatedListing | null;
}

export function useNewListingForm(onCreated?: (listing: CreatedListing) => void): UseNewListingForm {
  const [created, setCreated] = useState<CreatedListing | null>(null);
  const queryClient = useQueryClient();

  const machine = useListingForm({
    initialValues: useMemo(() => emptyFormValues(), []),
    submit: async (value) => {
      const listing = await runCreateListing(toCreateInput(value));
      // The browse list is cached (30s staleTime); mark it stale or the new listing won't show.
      // Creating also consumes a cap slot, so refresh the billing status the affordances read.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: LISTINGS_QUERY_PREFIX }),
        queryClient.invalidateQueries({ queryKey: BILLING_STATUS_QUERY_KEY }),
      ]);
      setCreated(listing);
      onCreated?.(listing);
    },
  });

  return useMemo(() => ({ ...machine, created }), [machine, created]);
}

export function toCreateInput(values: NewListingFormValues): CreateListingInput {
  const window =
    values.bookingWindowMode === BookingWindowMode.Date
      ? { bookingUntil: values.bookingUntil }
      : { bookingHorizonDays: values.bookingHorizonDays };
  return CreateListingInputSchema.parse({
    address: values.address,
    showingNotes: values.showingNotes.trim() === '' ? undefined : values.showingNotes,
    ...window,
    unavailability: values.unavailability,
    bookingAccess: values.bookingAccess,
    notifyUserIds: values.notifyUserIds,
  });
}
